import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { Calendar, User, ArrowRight, BookOpen } from "lucide-react";
import ShareButtons from "@/components/ShareButtons";

interface BlogPost {
  id: string;
  title: string;
  content: string;
  excerpt: string;
  image_url: string;
  created_at: string;
  author_id: string;
}

const Blog = () => {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const { data, error } = await supabase
        .from('blog_posts_2025_11_10_14_11')
        .select('*')
        .eq('is_published', true)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setPosts(data || []);
    } catch (error: any) {
      toast({
        title: "Error",
        description: "No se pudieron cargar los posts del blog",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardHeader>
                <div className="h-48 bg-muted rounded-lg"></div>
                <div className="h-4 bg-muted rounded w-3/4"></div>
                <div className="h-3 bg-muted rounded w-1/2"></div>
              </CardHeader>
              <CardContent>
                <div className="h-3 bg-muted rounded w-full mb-2"></div>
                <div className="h-3 bg-muted rounded w-2/3"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold mb-4 bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
          Blog de Amelba Soluciones
        </h1>
        <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
          Mantente al día con las últimas tendencias en streaming, producción audiovisual y tecnología.
        </p>
      </div>

      {/* Featured Post */}
      {posts.length > 0 && (
        <div className="mb-12">
          <Card className="overflow-hidden">
            <div className="md:flex">
              <div className="md:w-1/2">
                <div className="h-64 md:h-full bg-muted">
                  {posts[0].image_url ? (
                    <img 
                      src={posts[0].image_url} 
                      alt={posts[0].title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <BookOpen className="h-16 w-16 text-muted-foreground" />
                    </div>
                  )}
                </div>
              </div>
              <div className="md:w-1/2 p-6">
                <Badge className="mb-4 bg-gradient-to-r from-primary to-accent text-white border-0">
                  Artículo Destacado
                </Badge>
                <CardTitle className="text-2xl mb-4">{posts[0].title}</CardTitle>
                <CardDescription className="text-base mb-4">
                  {posts[0].excerpt || posts[0].content.substring(0, 200) + '...'}
                </CardDescription>
                <div className="flex items-center text-sm text-muted-foreground mb-4">
                  <Calendar className="h-4 w-4 mr-2" />
                  {formatDate(posts[0].created_at)}
                  <User className="h-4 w-4 ml-4 mr-2" />
                  Amelba Team
                </div>
                <Link to={`/blog/${posts[0].id}`}>
                  <button className="inline-flex items-center text-primary hover:text-primary/80 font-medium">
                    Leer más
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </button>
                </Link>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Posts Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {posts.slice(1).map((post) => (
          <Card key={post.id} className="group hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
            <CardHeader>
              <div className="h-48 rounded-lg overflow-hidden mb-4 bg-muted">
                {post.image_url ? (
                  <img 
                    src={post.image_url} 
                    alt={post.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <BookOpen className="h-16 w-16 text-muted-foreground" />
                  </div>
                )}
              </div>
              <CardTitle className="text-lg line-clamp-2">{post.title}</CardTitle>
              <CardDescription className="line-clamp-3">
                {post.excerpt || post.content.substring(0, 150) + '...'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center text-sm text-muted-foreground mb-4">
                <Calendar className="h-4 w-4 mr-2" />
                {formatDate(post.created_at)}
              </div>
              <Link to={`/blog/${post.id}`}>
                <button className="inline-flex items-center text-primary hover:text-primary/80 font-medium">
                  Leer más
                  <ArrowRight className="ml-2 h-4 w-4" />
                </button>
              </Link>
            </CardContent>
          </Card>
        ))}
      </div>

      {posts.length === 0 && !loading && (
        <div className="text-center py-12">
          <BookOpen className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-xl font-semibold mb-2">No hay posts disponibles</h3>
          <p className="text-muted-foreground">Pronto publicaremos contenido interesante en nuestro blog.</p>
        </div>
      )}

      {/* Newsletter CTA */}
      <div className="mt-16 text-center bg-gradient-to-r from-primary/10 to-accent/10 rounded-lg p-8">
        <h2 className="text-2xl font-bold mb-4">¿Quieres estar al día?</h2>
        <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
          Suscríbete a nuestro newsletter para recibir las últimas noticias, 
          tutoriales y consejos sobre streaming y producción audiovisual.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center max-w-md mx-auto">
          <input 
            type="email" 
            placeholder="Tu email" 
            className="flex-1 px-4 py-2 rounded-lg border border-input bg-background"
          />
          <button className="px-6 py-2 bg-gradient-to-r from-primary to-accent text-white rounded-lg hover:opacity-90 transition-opacity">
            Suscribirse
          </button>
        </div>
      </div>
    </div>
  );
};

export default Blog;